'use client';

import { z } from 'zod';

import CallToAction from '~/components/atoms/CallToAction';
import Heading from '~/components/atoms/Heading';
import DiagnosticsGraph from '~/components/molecules/DiagnosticsGraph';
import Dialog from '~/components/molecules/Dialog';
import { modelProperty } from '~/lib/api/models';
import { notNullable } from '~/lib/utils/runtime-type';

export interface Props {
  data: z.infer<typeof modelProperty.schema>['property']['propertyMeta'];
}

const DiagnosticsDialog: React.FC<Props> = ({ data }) => {
  const hasDiagnostic: boolean =
    Boolean(data.diagnosticDpeLetter) &&
    notNullable(data.diagnosticDpeValue) &&
    Boolean(data.diagnosticGesLetter) &&
    notNullable(data.diagnosticGesValue);

  const hasFooterData: boolean =
    Boolean(data.diagnosticDate) ||
    Boolean(data.diagnosticVersion) ||
    Boolean(data.diagnosticMinAnnualCost) ||
    Boolean(data.diagnosticMaxAnnualCost);

  return (
    <Dialog
      trigger={(open) => (
        <CallToAction level={2} onClick={open}>
          DPE
        </CallToAction>
      )}
      className="text-brand-primary max-w-3xl"
    >
      <Heading as="h2" level={3}>
        Détail des diagnostics énergétiques
      </Heading>

      {hasDiagnostic ? (
        <>
          <div className="my-8 flex flex-col gap-y-2">
            <Heading as="h3" level={4}>
              Diagnostic de performance énergétique (DPE)
            </Heading>
            <DiagnosticsGraph type="dpe" data={data} />
          </div>

          <div className="my-8 flex flex-col gap-y-2">
            <Heading as="h3" level={4}>
              Indice d’émission de gaz à effet de serre (GES)
            </Heading>
            <DiagnosticsGraph type="ges" data={data} />
          </div>

          {hasFooterData && (
            <div className="relative flex flex-col gap-y-3">
              {data.diagnosticMinAnnualCost && data.diagnosticMaxAnnualCost && (
                <p className="text-sm text-pretty">
                  Montant moyen estimé des dépenses annuelles d’énergie pour un
                  usage standard
                  {data.diagnosticAnnualCostReferenceYear &&
                    `, établi à partir des prix de l’énergie de l’année ${data.diagnosticAnnualCostReferenceYear}`}
                   : Entre {data.diagnosticMinAnnualCost} € et{' '}
                  {data.diagnosticMaxAnnualCost}
                   €
                </p>
              )}

              <div className="text-brand-quinary relative flex flex-col text-xs">
                {data.diagnosticDate && (
                  <p>
                    Date de réalisation du diagnostic : {data.diagnosticDate}
                  </p>
                )}

                {data.diagnosticVersion && (
                  <p>Version du diagnostic : {data.diagnosticVersion}</p>
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="py-20 text-center">
          <Heading as="p" level={4} className="text-brand-quinary">
            DPE vierge
          </Heading>
        </div>
      )}
    </Dialog>
  );
};

export default DiagnosticsDialog;
